const isSuccessfulResponse = (response) =>
  response?.status === 200 || response?.status === 201;

export const getCreatedCompanyId = (response) => {
  const companyId =
    response?.data?.result?._id ??
    response?.data?._id ??
    response?.data?.id ??
    "";

  return typeof companyId === "string" && /^[a-f\d]{24}$/i.test(companyId)
    ? companyId
    : "";
};

export const getManufacturerFaviconErrorCode = (error) =>
  error?.response?.data?.errorCode ?? error?.response?.data?.code ?? "";

const keepResult = {
  action: "keep",
  faviconApplied: true,
  mutationAttempted: false,
  errorCode: "",
};

export const retryManufacturerFaviconMutation = async ({
  intent,
  manufacturerId,
  file,
  upload,
  remove,
  isDentalLaboratory = false,
}) => {
  if (isDentalLaboratory || intent === "keep") return { ...keepResult };

  try {
    if (intent === "replace") {
      const payload = await upload(manufacturerId, file);
      return {
        action: intent,
        faviconApplied: true,
        mutationAttempted: true,
        payload,
        errorCode: "",
      };
    }

    if (intent === "delete") {
      const payload = await remove(manufacturerId);
      return {
        action: intent,
        faviconApplied: true,
        mutationAttempted: true,
        payload,
        errorCode: "",
      };
    }

    return { ...keepResult };
  } catch (error) {
    return {
      action: intent,
      faviconApplied: false,
      mutationAttempted: true,
      error,
      errorCode: getManufacturerFaviconErrorCode(error),
    };
  }
};

export const runManufacturerFaviconCreation = async ({
  response,
  file,
  isDentalLaboratory,
  upload,
}) => {
  const manufacturerId = getCreatedCompanyId(response);

  if (isDentalLaboratory || !file) {
    return {
      companySaved: true,
      manufacturerId,
      ...keepResult,
    };
  }

  if (!manufacturerId) {
    return {
      companySaved: true,
      manufacturerId: "",
      action: "replace",
      faviconApplied: false,
      mutationAttempted: false,
      invalidManufacturerId: true,
      errorCode: "",
    };
  }

  const mutationResult = await retryManufacturerFaviconMutation({
    intent: "replace",
    manufacturerId,
    file,
    upload,
  });

  return {
    companySaved: true,
    manufacturerId,
    ...mutationResult,
  };
};

export const runManufacturerFaviconUpdate = async ({
  saveCompany,
  intent,
  manufacturerId,
  file,
  upload,
  remove,
  isDentalLaboratory = false,
}) => {
  let response;

  try {
    response = await saveCompany();
  } catch (error) {
    return {
      companySaved: false,
      faviconApplied: false,
      mutationAttempted: false,
      error,
      errorCode: getManufacturerFaviconErrorCode(error),
    };
  }

  if (!isSuccessfulResponse(response)) {
    return {
      companySaved: false,
      faviconApplied: false,
      mutationAttempted: false,
      response,
      errorCode: "",
    };
  }

  const mutationResult = await retryManufacturerFaviconMutation({
    intent,
    manufacturerId,
    file,
    upload,
    remove,
    isDentalLaboratory,
  });

  return {
    companySaved: true,
    response,
    ...mutationResult,
  };
};

export const releaseObjectUrl = (previewUrl, revokeObjectUrl) => {
  if (previewUrl?.startsWith("blob:")) revokeObjectUrl(previewUrl);
  return "";
};

export const finalizePreviewBeforeReload = ({
  previewUrl,
  revokeObjectUrl,
  setPreviewUrl,
  reload,
}) => {
  const clearedPreviewUrl = releaseObjectUrl(previewUrl, revokeObjectUrl);
  setPreviewUrl(clearedPreviewUrl);
  reload();
};
